import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Slot as SlotPrimitive } from 'radix-ui';
import { createContext, useContext } from 'react';
import * as React from 'react';
// Context to track whether scoping is enabled
// Used by portal-based components to apply .ds-scope wrapper when needed
const DesignSystemScopeContext = createContext(false);
/**
 * Hook to access the current design system scope state
 * @internal Used by PortalScope component
 */
export function useDesignSystemScope() {
    return useContext(DesignSystemScopeContext);
}
/**
 * DesignSystemProvider wraps design system components for CSS isolation
 *
 * **When to use:**
 * - When using scoped.css in apps with existing design systems (Material UI, Vaporwave)
 * - Prevents CSS conflicts by applying .ds-scope wrapper class
 *
 * **When NOT to use:**
 * - When using design-system.css or core.css (no scoping needed)
 * - In standalone apps without existing design systems
 *
 * **Context Propagation:**
 * - Always provides `DesignSystemScopeContext` to enable portal scoping
 * - When `scoped={false}`, context value is `false` (portals won't add `.ds-scope`)
 * - When nested with different `scoped` values, inner value takes precedence
 *
 * @example
 * ```tsx
 * // Basic usage: adds a wrapping div with .ds-scope
 * <DesignSystemProvider>
 *   <Button>Design System Button</Button>
 * </DesignSystemProvider>
 *
 * // No scoping needed (using design-system.css or core.css)
 * <DesignSystemProvider scoped={false}>
 *   <Button>Design System Button</Button>
 * </DesignSystemProvider>
 *
 * // Merge with existing element (no extra div)
 * <DesignSystemProvider asChild>
 *   <main className="flex">
 *     <Button>Design System Button</Button>
 *   </main>
 * </DesignSystemProvider>
 * // Renders: <main class="ds-scope flex">...</main>
 *
 * // Preserve flex/grid layout
 * <div className="flex gap-4">
 *   <DesignSystemProvider asChild>
 *     <section>
 *       <Button>Button 1</Button>
 *     </section>
 *   </DesignSystemProvider>
 * </div>
 * ```
 *
 * @see ADR-5: CSS-Level Scoping Mechanism
 * @see ADR-4: Four CSS Variant Strategy
 * @see ADR-11: Automatic Portal Scoping via Context
 */
export function DesignSystemProvider({ children, asChild = false, scoped = true, className, ...props }) {
    // Build className with ds-scope if scoped
    const scopedClassName = scoped ? `ds-scope ${className || ''}`.trim() : className;
    // Always render Context.Provider to enable portal scoping
    // When scoped=true: render wrapper div with ds-scope
    // When scoped=false: render bare children (but context value is false)
    return (_jsx(DesignSystemScopeContext.Provider, { value: scoped, children: scoped ? (_jsx(_Fragment, { children: asChild ? (_jsx(SlotPrimitive.Slot, { className: scopedClassName, ...props, children: children })) : (_jsx("div", { className: scopedClassName, ...props, children: children })) })) : (
        // Not scoped, but context still provides the scoped=false value
        _jsx(_Fragment, { children: children })) }));
}
